import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Globe, CheckCircle2, XCircle, Search } from "lucide-react";
import { extractCandidatesFromText } from "@/utils/candidateExtractor";
import { getApiUrl } from "@/utils/apiConfig";

interface WebImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (data: any[]) => void;
}

export function WebImportDialog({ isOpen, onClose, onImport }: WebImportDialogProps) {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [error, setError] = useState("");

  const handleScrape = async () => {
    if (!url || !url.startsWith("http")) {
      setError("Veuillez entrer une URL valide (commençant par http ou https).");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      const token = localStorage.getItem('kaynin_auth_token') || "";
      const res = await fetch(`${getApiUrl()}/candidates.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify({ action: "scrape_url", url })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.error || "Erreur de récupération.");
      }
      
      const text = data.text;
      const candidates = extractCandidatesFromText(text);
      setPreviewData(candidates);
      
      if (candidates.length === 0) {
         setError("L'URL a été lue, mais aucun candidat n'a pu être extrait avec certitude. Vérifiez le lien ou utilisez l'import Excel.");
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Impossible de scrapper l'URL. Le site bloque peut-être les requêtes.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    if (previewData.length > 0) {
      onImport(previewData);
      handleClose();
    }
  };

  const handleClose = () => {
    setUrl("");
    setPreviewData([]);
    setError("");
    onClose();
  };

  const updateCandidate = (index: number, field: string, value: string) => {
    const updated = [...previewData];
    updated[index][field] = value;
    setPreviewData(updated);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[900px] bg-background">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-indigo-600" /> Import depuis un lien Web
          </DialogTitle>
          <DialogDescription>
            Collez le lien vers un article de presse ou un site officiel contenant la liste des candidats (Français ou Arabe).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="url"
                placeholder="https://www.exemple.ma/liste-candidats-2026..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="w-full h-10 pl-9 pr-4 rounded-xl border border-input bg-background outline-none focus:ring-2 focus:ring-primary/20 text-sm"
              />
            </div>
            <Button onClick={handleScrape} disabled={loading || !url} className="h-10 rounded-xl px-5 bg-indigo-600 hover:bg-indigo-700">
              {loading ? "Recherche..." : "Extraire"}
            </Button>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-lg">
              <XCircle className="w-4 h-4" /> {error}
            </div>
          )}

          {!loading && previewData.length > 0 && (
            <div className="space-y-3 mt-4">
              <div className="flex items-center justify-between text-sm">
                <span className="font-semibold flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  {previewData.length} candidats détectés
                </span>
              </div>

              <div className="border border-border rounded-lg overflow-hidden max-h-[400px] overflow-y-auto">
                <table className="w-full text-xs text-left">
                  <thead className="bg-muted/50 sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-2 w-1/4">Nom Complet (FR)</th>
                      <th className="px-3 py-2 w-1/4">Nom Complet (AR)</th>
                      <th className="px-3 py-2">Région / Province</th>
                      <th className="px-3 py-2">Parti</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {previewData.slice(0, 50).map((c, i) => (
                      <tr key={i} className="hover:bg-muted/20">
                        <td className="px-3 py-2 font-medium">
                           <div className="flex gap-1">
                             <input type="text" value={c.firstName} onChange={e => updateCandidate(i, 'firstName', e.target.value)} className="w-full bg-background border border-border rounded px-2 py-1" placeholder="Prénom" />
                             <input type="text" value={c.lastName} onChange={e => updateCandidate(i, 'lastName', e.target.value)} className="w-full bg-background border border-border rounded px-2 py-1" placeholder="Nom" />
                           </div>
                        </td>
                        <td className="px-3 py-2 font-medium">
                           <div className="flex gap-1">
                             <input type="text" value={c.firstNameAr} onChange={e => updateCandidate(i, 'firstNameAr', e.target.value)} className="w-full bg-background border border-border rounded px-2 py-1 text-right" placeholder="الاسم" dir="rtl" />
                             <input type="text" value={c.lastNameAr} onChange={e => updateCandidate(i, 'lastNameAr', e.target.value)} className="w-full bg-background border border-border rounded px-2 py-1 text-right" placeholder="النسب" dir="rtl" />
                           </div>
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{c.region} - {c.province}</td>
                        <td className="px-3 py-2">{c.partyAcronym}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {previewData.length > 50 && <p className="text-xs text-center text-muted-foreground pt-2">+ {previewData.length - 50} autres candidats</p>}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <Button variant="outline" onClick={handleClose}>Annuler</Button>
          <Button onClick={handleConfirm} disabled={previewData.length === 0 || loading} className="bg-indigo-600 hover:bg-indigo-700 text-white">
            Importer les données
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
